import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { T } from "services/lang/LanguageService";

import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import DatasetCategoryDropdown from "components/basic/dropdowns/dataset-category/DatasetCategoryDropdown";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";

import {
  useCreateCategorizedDataset,
  useGetDefaultDatasetCategory,
} from "api/api-hooks/datasets-hooks";
import { BaseDatasetDTO } from "api/model/dataset/BaseDatasetDTO";
import SimpleJsonExportButton from "components/basic/simple-json-export-button/SimpleJsonExportButton";
import { FormattedDataForExportProps } from "components/basic/datasets-properties/DatasetsPropertiesTable";
import {
  DATASET_ACCESS_TYPES,
  DATASET_DATA_TYPES,
} from "components/dashboard/dataset-window/utils/utils";
import { DATASET_DEFAULT_DIALOG_STATE } from "../create-multiple-datasets/CreateMultipleDatasets";

interface CreateDialogProps {
  closeDialog: () => void;
}

const CreateSingleDataset = ({ closeDialog }: CreateDialogProps) => {
  const [unitSelectorValue, setUnitSelectorValue] = useState(
    DATASET_DEFAULT_DIALOG_STATE.unitSelectorValue
  );
  const [datasetTitle, setDatasetTitle] = useState(DATASET_DEFAULT_DIALOG_STATE.datasetTitle);
  const [dataType, setDataType] = useState(DATASET_DEFAULT_DIALOG_STATE.dataType);
  const [accessType, setAccessType] = useState(DATASET_DEFAULT_DIALOG_STATE.accessType);
  const [defaultCategory, setDefaultCategory] = useState<number | undefined>(
    DATASET_DEFAULT_DIALOG_STATE.defaultCategory
  );
  const [createdDataset, setCreatedDataset] = useState<BaseDatasetDTO | undefined>(undefined);
  const [formattedDataForExport, setFormattedDataForExport] = useState<
    Array<FormattedDataForExportProps>
  >([]);

  const defaultCategoryQuery = useGetDefaultDatasetCategory();

  useEffect(() => {
    if (createdDataset) {
      setFormattedDataForExport(prevData => [
        ...prevData,
        {
          dataset: createdDataset.identifier,
          name: createdDataset.title,
          group: createdDataset.groupTitle,
          dataType: createdDataset.dataType,
          unit: createdDataset.unit,
          category: createdDataset.categoryTitle,
          datasetClass: createdDataset.datasetClass,
          accessType: createdDataset.accessType,
          decimalPlaces: createdDataset.decimalPlaces,
          tags: createdDataset.tags,
        },
      ]);
    }
  }, [createdDataset]);

  useEffect(() => {
    const dataset = sessionStorage.getItem("dataset");
    if (dataset) {
      const parsedDataset = JSON.parse(dataset);
      setUnitSelectorValue(parsedDataset.unit);
      setDatasetTitle(parsedDataset.title);
      setDataType(parsedDataset.dataType);
      setAccessType(parsedDataset.accessType);
      setDefaultCategory(parsedDataset.categoryId);
    }
  }, []);

  useEffect(() => {
    if (defaultCategoryQuery.isFetched) {
      setDefaultCategory(defaultCategoryQuery.data?.id);
    }
    if (defaultCategoryQuery.isError) {
      easasAlert({ summary: T("error.error-fetching-category") });
    }
  }, [defaultCategoryQuery.data]);

  const { mutate } = useCreateCategorizedDataset();

  function createDatasetHandler() {
    if (datasetTitle.length === 0) {
      easasAlert({
        summary: T("alert.title-missing"),
        detail: T("alert.please-enter-a-title"),
        severity: "error",
      });
      return;
    }

    const data: Partial<BaseDatasetDTO> = {
      title: datasetTitle,
      categoryId: defaultCategory,
      dataType,
      accessType,
      unit: unitSelectorValue,
    };

    sessionStorage.setItem("dataset", JSON.stringify(data));

    mutate(data, {
      onSuccess: response => {
        setCreatedDataset(response);
        easasAlert({
          summary: T("alert.dataset-created"),
          detail: T("alert.has-been-created", { title: response?.title }),
          severity: "success",
        });
      },
      onError: error => {
        easasAlert({ summary: T("alert.creation-failed"), detail: `${error}`, severity: "error" });
        closeDialog();
      },
    });
  }

  const renderJsonExportButton = () => (
    <SimpleJsonExportButton data={formattedDataForExport} filename="dataset-properties.json" />
  );

  const copyTextOnClick = (text: string, copyableText: string) => {
    return <CopyTextWithButton text={text} copyableText={copyableText} />;
  };

  return (
    <div className="grid">
      {createdDataset ? (
        <div className="flex col-12 align-items-center">
          <DataTable value={[createdDataset]} className="w-full">
            <Column
              field="title"
              header={T("word.title")}
              body={e => copyTextOnClick(e.title, e.title)}
            />
            <Column
              field="identifier"
              header={T("word.identifier")}
              body={e => copyTextOnClick(e.identifier, e.identifier)}
            />
            <Column field="export" header={T("action.export")} body={renderJsonExportButton} />
          </DataTable>
        </div>
      ) : (
        <>
          <div className="flex col-12 align-items-center">
            <p className="col-4 mb-0">{T("word.category")}</p>
            <DatasetCategoryDropdown
              className="w-full"
              value={defaultCategory}
              onChange={e => setDefaultCategory(e)}
            />
          </div>
          <div className="flex col-12 align-items-center">
            <p className="col-4 mb-0">{T("word.dataset-title")}</p>
            <InputText
              className="w-full"
              value={datasetTitle}
              onChange={e => setDatasetTitle(e.target.value)}
              placeholder={T("label.new-dataset-title")}
            />
          </div>
          <div className="flex col-12 align-items-center">
            <p className="col-4 mb-0">{T("word.dataset-type")}</p>
            <Dropdown
              className="w-full"
              options={DATASET_DATA_TYPES}
              value={dataType}
              onChange={e => setDataType(e.value)}
              showClear
            />
          </div>
          <div className="flex col-12 align-items-center">
            <p className="col-4 mb-0">{T("label.data-type")}</p>
            <Dropdown
              className="w-full"
              options={DATASET_ACCESS_TYPES}
              value={accessType}
              onChange={e => setAccessType(e.value)}
              showClear
            />
          </div>
          <div className="flex col-12 align-items-center">
            <p className="col-4 mb-0">{T("word.unit")}</p>
            <DatasetUnitSelector
              className="w-full"
              value={unitSelectorValue}
              onChange={e => setUnitSelectorValue(e.value)}
            />
          </div>
          <div className="flex col-12 gap-2 justify-content-end">
            <Button
              className="component-editor-button icon-red"
              icon="pi pi-times"
              label={T("word.cancel")}
              onClick={closeDialog}
            />
            <Button
              className="component-editor-button icon-green"
              icon="pi pi-check"
              label={T("action.create")}
              onClick={() => createDatasetHandler()}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CreateSingleDataset;
